import React, { useRef } from "react";
import type { ColorSource, FederatedEventHandler, Graphics as PixiGraphics } from "pixi.js";
import { colorNameMap, edstFontName, eramFontDimensionMap, getBitmapTextStyles } from "@poscon/shared-frontend";
import { Rectangle } from "pixi.js";

const fontName = edstFontName;
const fontDimension = eramFontDimensionMap[fontName];

export const titleBarHeight = fontDimension.height + 6;

function drawBlock(
  graphics: PixiGraphics,
  fillColor: ColorSource,
  topLeftBlockColor: ColorSource,
  bottomRightBlockColor: ColorSource,
  width = titleBarHeight - 2,
  height = titleBarHeight - 2,
  x = 0,
  y = 0,
) {
  graphics.rect(x, y, width, height).fill(fillColor).stroke({ width: 1, color: topLeftBlockColor });
  graphics
    .moveTo(x + width, y)
    .lineTo(x + width, y + height)
    .lineTo(x, y + height)
    .stroke({ width: 1, color: bottomRightBlockColor });
}

type WindowTitleBarProps = {
  focused: boolean;
  closeView: () => void;
  toggleFullscreen: () => void;
  startDrag: FederatedEventHandler;
  text: string[];
  width: number;
};

/**
 * Window title bar for various components
 */
export const ViewTitleBar = ({
  focused,
  text,
  closeView,
  toggleFullscreen,
  startDrag,
  width,
}: WindowTitleBarProps) => {
  const leftRef = useRef<PixiGraphics>(null);
  const toggleFullscreenRef = useRef<PixiGraphics>(null);
  const closeRef = useRef<PixiGraphics>(null);

  const fillColor = focused ? 0x008585 : colorNameMap.mediumGrey;
  const topLeftBlockColor = 0xadadad;
  const bottomRightBlockColor = focused ? 0x005757 : 0x575757;

  const headerText = text.map((s) => s).join("  ");

  return (
    <container x={2} y={2}>
      <graphics
        ref={leftRef}
        draw={(graphics) => {
          graphics.clear();
          drawBlock(graphics, fillColor, topLeftBlockColor, bottomRightBlockColor);
          drawBlock(
            graphics,
            fillColor,
            topLeftBlockColor,
            bottomRightBlockColor,
            9,
            3,
            Math.floor((titleBarHeight - 2) / 2) - 4,
            Math.floor((titleBarHeight - 2) / 2) - 1,
          );
        }}
        eventMode="static"
        onMouseDown={toggleFullscreen}
      />
      <graphics
        x={titleBarHeight - 2 + 1}
        draw={(graphics) => {
          graphics.clear();
          drawBlock(
            graphics,
            fillColor,
            topLeftBlockColor,
            bottomRightBlockColor,
            width - titleBarHeight * 3 + 1,
          );
        }}
        eventMode="static"
        onMouseDown={startDrag}
      />
      <bitmapText
        text={headerText}
        eventMode="none"
        x={Math.floor((width - headerText.length * fontDimension.width) / 2)}
        y={Math.floor((titleBarHeight - fontDimension.height) / 2)}
        style={{ ...getBitmapTextStyles(fontName), fill: 0 }}
      />
      <graphics
        x={width - titleBarHeight * 2 + 1}
        ref={closeRef}
        draw={(graphics) => {
          graphics.clear();
          drawBlock(graphics, fillColor, topLeftBlockColor, bottomRightBlockColor);
          drawBlock(
            graphics,
            fillColor,
            topLeftBlockColor,
            bottomRightBlockColor,
            3,
            3,
            Math.floor((titleBarHeight - 2) / 2) - 1,
            Math.floor((titleBarHeight - 2) / 2) - 1,
          );
        }}
        hitArea={new Rectangle(0, 0, titleBarHeight, titleBarHeight)}
        eventMode="static"
        onMouseDown={closeView}
      />
      <graphics
        x={width - titleBarHeight}
        ref={toggleFullscreenRef}
        draw={(graphics) => {
          graphics.clear();
          drawBlock(graphics, fillColor, topLeftBlockColor, bottomRightBlockColor);
          drawBlock(
            graphics,
            fillColor,
            topLeftBlockColor,
            bottomRightBlockColor,
            9,
            9,
            Math.floor((titleBarHeight - 8) / 2) - 1,
            Math.floor((titleBarHeight - 8) / 2) - 1,
          );
        }}
        hitArea={new Rectangle(0, 0, titleBarHeight, titleBarHeight)}
        eventMode="static"
        onMouseDown={toggleFullscreen}
      />
    </container>
  );
};
